<template>
  <div>
    <v-app dark>
      <Loader v-if="!hasInitialData" />

      <v-row
        v-if="hasInitialData"
        class="no-gutters"
      >
        <transition>
          <keep-alive>
            <router-view />
          </keep-alive>
        </transition>
      </v-row>
    </v-app>
  </div>
</template>

<script>
import { checkForAuthentication, logoutAndSendUserTo403Page } from '@stilingue/st-auth-utils/StAuthUtils';

import { insertMixpanelPlugin } from '@/plugins/mixpanel';
import { insertPluginMetadata } from '@/plugins/insert_plugins_metadata';

import { Loader } from '@/components/base';
import { HandleCookies, getUrlParameter } from '@/utils';

export default {
  name: 'App',
  components: {
    Loader,
  },
  data() {
    return {
      hasInitialData: false,
    };
  },
  async created() {
    this.handleRemoveCookies();
    await this.handleAuthentication();

    this.hasInitialData = true;
  },
  methods: {
    checkForAuthentication,
    handleRemoveCookies() {
      HandleCookies.removeCookies({ name: 'plan-id' });
      HandleCookies.removeCookies({ name: 'benchmarks-id' });
    },
    async loadUsers() {
      await this.$store.dispatch('user/getUserMe');
    },
    async loadCompanies() {
      return this.$store.dispatch('company/getCompanies');
    },
    configurePlugins() {
      insertMixpanelPlugin(
        this.$store.state.user.user,
        this.$store.state.company.activeCompany.name
      );

      insertPluginMetadata({
        user: this.$store.state.user.user,
        activeCompany: this.$store.state.company.activeCompany,
      });
    },
    sendToLoginPage() {
      let redirect = encodeURIComponent(window.location.href);
      if (this.$route && this.$route.query && this.$route.query.np_red) {
        redirect = this.$route.query.np_red;
      }

      const url = `${process.env.VUE_APP_STILINGUE_LOGIN_SERVICE}login?np_red=${redirect}`;

      window.location.href = url;
    },
    sendToSelectCompaniesPage() {
      return this.$router.push({
        name: 'companies',
      }).catch(() => null);
    },
    handleChooseActiveCompany(companyList) {
      const [stilingueMasters, stilingue] = [5703116783878144, 4816908371099648];
      const hasUserGroupAdmin = companyList.find((c) => c.id === stilingueMasters || c.id === stilingue);

      if (hasUserGroupAdmin) {
        this.$store.dispatch('company/addActiveCompany', hasUserGroupAdmin);
        return;
      }

      const planIdParameter = getUrlParameter('plan_id');
      const companyFound = companyList.find(({ id }) => id === parseInt(planIdParameter, 10));

      if (companyList.length > 1 && companyFound) {
        this.$store.dispatch('company/addActiveCompany', companyFound);
        return;
      }

      this.$store.dispatch('company/addActiveCompany', companyList[0]);
    },
    async handleAuthentication() {
      try {
        this.hasInitialData = false;
        const authenticated = this.checkForAuthentication();

        if (!authenticated) {
          return this.sendToLoginPage();
        }

        await this.loadUsers();
        const companyList = await this.loadCompanies();
        const planIdParameter = getUrlParameter('plan_id');

        if (companyList.length === 0) {
          return logoutAndSendUserTo403Page();
        }

        if (companyList.length > 1 && !planIdParameter) {
          return this.sendToSelectCompaniesPage(companyList);
        }

        this.handleChooseActiveCompany(companyList);
        this.configurePlugins();
      } catch (e) {
        this.$router.push({
          name: 'access-denied',
          params: { errorStatus: '500' },
        });

        console.error(e);
      }
    },
  },
};
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css?family=Open+Sans:300,400,600,700,800");
@import "~vuejs-noty/dist/vuejs-noty.css";
@import "./plugins/v-tooltip/v-tooltip.scss";
@import "~@stilingue/st-styles/main.scss";
@import "./styles/main.scss";

* {
  font-family: var(--ds-font-family);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  text-align: center;
  background: var(--ds-color-brand-3);
}

html {
  overflow-y: auto !important;
}

.disabled__container {
  > * {
    opacity: var(--ds-opacity-intense);
    cursor: default;
    pointer-events: none;
  }
}
</style>
